import { absoluteUrl, articleJsonLd, ogImage, seoLinks, clampTitle, clampDescription } from "@/lib/seo";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { BlogArticle } from "@/components/site/BlogViews";
import { CtaBand } from "@/components/site/Page";
import { getPublishedPost } from "@/lib/blog.functions";

export const Route = createFileRoute("/blogg/$slug")({
  loader: async ({ params }) => {
    const post = await getPublishedPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => {
    const title = clampTitle(loaderData?.seoTitle || loaderData?.title || "Blogg – Swedish International School");
    const description = clampDescription(loaderData?.seoDescription || loaderData?.excerpt || "Ett inlägg från Swedish International School.");
    const path = `/blogg/${loaderData?.slug ?? ""}`;
    const cover = loaderData?.coverImageUrl || "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absoluteUrl(path) },
        { name: "twitter:card", content: "summary_large_image" },
        ...(cover ? ogImage(`${cover}${cover.includes("?") ? "&" : "?"}w=1280`) : ogImage()),
        { property: "og:locale", content: "sv_SE" },
      ],
      links: [
        ...seoLinks(path),
        // Omslagsbilden är sidans LCP – hämta den direkt i stället för efter hydrering.
        ...(cover
          ? [
              {
                rel: "preload",
                as: "image",
                href: `${cover}${cover.includes("?") ? "&" : "?"}w=1280`,
                fetchpriority: "high",
              },
            ]
          : []),
      ],
      scripts: loaderData
        ? [
            articleJsonLd({
              path,
              headline: loaderData.title,
              description,
              image: loaderData.coverImageUrl || undefined,
              datePublished: loaderData.publishedAt,
              dateModified: loaderData.updatedAt,
              authorName: loaderData.authorName || undefined,
              locale: "sv",
              type: "BlogPosting",
            }),
          ]
        : [],
    };
  },
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      Inlägget kunde inte laddas just nu.
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      Inlägget finns inte längre.
    </div>
  ),
  component: BlogPostPage,
});

function BlogPostPage() {
  const post = Route.useLoaderData();
  return (
    <>
      <BlogArticle post={post} locale="sv" />
      <CtaBand locale="sv" />
    </>
  );
}
